import { ChangeEvent, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack, { StackProps } from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';

import useResponsive from '~/hooks/useResponsive';
import { ROWS_PER_PAGE_OPTIONS } from '~/utils/common/constants';

import PaginationActions from './PaginationActions';

interface CardPaginationProps {
  resultCount: number;
  page: number;
  rowsPerPage: number;
  isDataTable?: boolean;
  showResultCount?: boolean;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  onChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  sx?: StackProps['sx'];
}

const MOBILE_SELECT_SX = {
  color: 'text.secondary',
  fontSize: '14px',
  '& .MuiSelect-icon': {
    height: '16px',
    width: '18px',
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    pl: 0,
  },
} as const;

const TABLE_PAGINATION_SX = {
  '& .MuiTablePagination-toolbar': { pl: 0 },
  '& .MuiTablePagination-spacer': { display: 'none' },
  '& .MuiToolbar-gutters': { minHeight: '32px' },
};

const CardPagination = ({
  resultCount,
  page,
  rowsPerPage,
  isDataTable = false,
  showResultCount = false,
  onChangePage,
  onChangeRowsPerPage,
  sx = undefined,
}: CardPaginationProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'hostingResourceCard.pagination',
  });
  const {
    palette: { action },
  } = useTheme();
  const { isMobile } = useResponsive();

  const isMobileView = isMobile();
  const isDataTableMobile = isMobileView && isDataTable;
  const shouldShowResultCount = !isMobileView || isDataTable;
  const rowsPerPageOptions = isMobileView ? [] : ROWS_PER_PAGE_OPTIONS;
  const labelRowsPerPage = isMobileView ? '' : t('resultsPerPage');
  const horizontalPadding = isDataTableMobile ? 2 : 3;
  const cardSpacing = isDataTableMobile ? 2 : 0;

  const handleMobileRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    onChangeRowsPerPage(
      event as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    );
  };

  // Memoize ActionsComponent to prevent props from not being updated properly
  const ActionsComponent = useMemo(
    () => (props: any) =>
      <PaginationActions {...props} isDataTable={isDataTable} />,
    [isDataTable],
  );

  return (
    <Stack
      spacing={cardSpacing}
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        px: { xs: horizontalPadding, sm: 3 },
        py: isDataTable ? 2 : 2.25,
        bgcolor: action.hover,
        ...sx,
      }}
    >
      {(shouldShowResultCount || showResultCount) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="results"
              values={{ count: resultCount }}
              components={{ b: <b /> }}
            />
          </Typography>
          {(isDataTableMobile || showResultCount) && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('resultsPerPage')}
              </Typography>
              <Select
                id="row-per-page-selection"
                value={rowsPerPage}
                onChange={handleMobileRowsPerPageChange}
                size="small"
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={MOBILE_SELECT_SX}
              >
                {ROWS_PER_PAGE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )}
        </Stack>
      )}
      {isDataTableMobile && <Divider sx={{ borderColor: '#EDEFF0' }} />}
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={resultCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={labelRowsPerPage}
        labelDisplayedRows={({ from, to }) =>
          isDataTableMobile ? '' : t('fromToResults', { from, to })
        }
        ActionsComponent={ActionsComponent}
        slotProps={{
          select: {
            IconComponent: KeyboardArrowDownRoundedIcon,
            sx: {
              ml: 0,
              mr: 3,
              '& .MuiSelect-icon': {
                right: 8,
                fontSize: '1rem',
              },
            },
          },
        }}
        sx={{
          color: 'text.secondary',
          width: isMobileView ? '100%' : undefined,
          ...TABLE_PAGINATION_SX,
        }}
      />
    </Stack>
  );
};

export default CardPagination;
